import { useContext } from 'react';

import { useRouter } from 'next/router';
import { KeyedMutator, useSWRConfig } from 'swr';

import { LoginParams } from '@api/auth/AuthApi';

import { AuthContext } from '@contexts/AuthContext';

import { User } from '@models/User';

import Endpoints from '@services/Endpoints';
import { appendQueryParams, QueryParam } from '@services/QueryParams';

import { SignupPayload } from '@util/AuthUtil';
import Logger from '@util/Logger';
import { isNotBlank } from '@util/StringUtil';
import { isDefined } from '@util/TypeGuards';

const logger = Logger.make('hooks/useAuth');
const useAuth = () => {
    const authContext = useContext(AuthContext);
    const router = useRouter();
    const { mutate } = useSWRConfig();
    const currentPath = router.asPath;
    const { user, loading: authLoading, ready: authReady } = authContext;
    const isLoggedIn = isDefined(user);

    const loginRedirect = async (options?: {
        continueUrl?: string | null;
        useContinueUrl?: boolean;
        skipProfileSetup?: boolean;
        message?: string | null;
        loginPath?: string;
    }) => {
        const {
            useContinueUrl = true,
            continueUrl = currentPath,
            skipProfileSetup = false,
            message,
            loginPath: _loginPath = '/login',
        } = options ?? {};
        let loginPath = _loginPath;
        if (useContinueUrl && continueUrl) {
            authContext.setContinueParams({ continueUrl, skipProfileSetup });
            if (isNotBlank(message)) {
                authContext.setMessage({ message });
            }
            loginPath = appendQueryParams(loginPath, {
                [QueryParam.CONTINUE_URL]: continueUrl,
                [QueryParam.CONTINUE_SKIP_SETUP]: `${skipProfileSetup}`,
                [QueryParam.MESSAGE]: message,
            });
        }

        await router.push(loginPath);
    };

    const revalidateUser: KeyedMutator<User> = async (
        data = (current) => current,
        shouldRevalidate = true,
    ): Promise<User | undefined> => {
        logger.debug('[revalidateUser]', { data, shouldRevalidate });
        return mutate<User>(Endpoints.user.info(), data, shouldRevalidate);
    };

    const submitLogin = async (params: LoginParams) => {
        return authContext.submitLogin(params);
    };

    const submitSignup = async (params: SignupPayload) => {
        return authContext.submitSignup(params);
    };

    /**
     * Copied from auth store
     * Handles redirecting the user after a successful login.
     * Attempts a primary redirect based on continueUrl and profile status.
     * Falls back to /labspace if the primary redirect fails.
     * @returns {Promise<boolean>} True if redirection was successful, false otherwise.
     */
    const loginSuccessRedirect = async (user: User): Promise<boolean> => {
        if (!user) {
            logger.warn('No user provided in login success redirect.');
            return false;
        }

        const originalContinueUrl = authContext.continueParams.continueUrl;
        const skipProfileSetup = authContext.continueParams.skipProfileSetup ?? false;
        const profileComplete = user.registration_completed ?? false;
        let determinedTargetUrl: string;
        let clearContextParams = true; // Assume true, set to false if profile setup is needed

        if (originalContinueUrl && (skipProfileSetup || profileComplete)) {
            determinedTargetUrl = originalContinueUrl;
            logger.debug(
                `[loginSuccessRedirect] Determined target: ${determinedTargetUrl} (continueUrl with skipProfileSetup or profileComplete)`,
            );
        } else if (!profileComplete) {
            determinedTargetUrl = '/setup-profile';
            clearContextParams = false; // Keep context params for profile setup flow
            logger.debug(`[loginSuccessRedirect] Determined target: ${determinedTargetUrl} (profile not complete)`);
        } else if (originalContinueUrl) {
            // This case implies profile IS complete, but skipProfileSetup was false (or not provided)
            // So, we still honor the originalContinueUrl.
            determinedTargetUrl = originalContinueUrl;
            logger.debug(
                `[loginSuccessRedirect] Determined target: ${determinedTargetUrl} (profile complete, using continueUrl)`,
            );
        } else {
            // Default redirect if no specific continueUrl or other conditions met
            determinedTargetUrl = '/labspace';
            logger.debug(`[loginSuccessRedirect] Determined target: ${determinedTargetUrl} (default)`);
        }

        try {
            logger.info(`[loginSuccessRedirect] Attempting to redirect to: ${determinedTargetUrl}`);
            await router.push(determinedTargetUrl);
            // If successful, clear relevant context parameters
            authContext.setMessage(null);
            if (clearContextParams) {
                authContext.setContinueParams({ continueUrl: null, skipProfileSetup: false });
            }
            logger.info(`[loginSuccessRedirect] Successfully redirected to ${determinedTargetUrl}`);
            return true;
        } catch (error) {
            logger.error(`[loginSuccessRedirect] Failed to redirect to ${determinedTargetUrl}:`, error);
            // Fallback redirect attempt
            try {
                logger.warn('[loginSuccessRedirect] Attempting fallback redirect to /labspace');
                await router.push('/labspace');
                // If fallback successful, clear context parameters
                authContext.setMessage(null);
                // Always clear continueParams on successful fallback to labspace,
                // as the original continue logic/target failed.
                authContext.setContinueParams({ continueUrl: null, skipProfileSetup: false });
                logger.info('[loginSuccessRedirect] Successfully redirected to /labspace (fallback)');
                return true;
            } catch (fallbackError) {
                logger.error('[loginSuccessRedirect] Fallback redirect to /labspace also failed:', fallbackError);
                // All redirection attempts failed.
                return false;
            }
        }
    };

    return {
        authContext,
        user,
        isLoggedIn,
        authReady,
        loginRedirect,
        authLoading,
        logout: authContext.logout,
        revalidateUser,
        submitLogin,
        submitSignup,
        loginSuccessRedirect,
        isPlutoUser: user?.email?.endsWith('@pluto.bio') ?? false,
    };
};

export default useAuth;
